import {
  GeoJSONFeature
} from 'maplibre-gl';

import * as CommonSelectors from './utils/common_selectors';
import doubleClickZoom from './utils/double_click_zoom';
import DrawLine from './utils/draw_line_string';
import isEventAtCoordinates from './utils/is_event_at_coordinates';

import {
  calculateSnapTolerance,
  clearSnapMarkerOnMap,
  sanitizeSnapOptions,
  setSnapMarkerOnMap,
  type SnapHitKind,
} from './utils/CalculateSnapTolerance';
import { positionToLngLat } from '../../Controls/featureCreateGeometry';
import * as Constants from './utils/constants';
// import {deegrees2meters, meters2degrees, lineAngle} from "../../utils";

import { drawModes, drawState, Source } from '../../Types';

const SnapLineMode:drawModes = <drawModes>{ ...DrawLine };

function isAtLastVertex(state: drawState, e: any, lng: number, lat: number): boolean {
  if (state.currentVertexPosition <= 0) return false;

  const lastVertex = state.line.coordinates[state.currentVertexPosition - 1] as [
    number,
    number,
  ];

  return (
    isEventAtCoordinates(e, lastVertex)
    || (lastVertex[0] === lng && lastVertex[1] === lat)
  );
}

function finishSnapLine(this: drawModes, state: drawState) {
  return this.changeMode(Constants.modes.SIMPLE_SELECT, {
    featureIds: [state.line.id],
  });
}

export default SnapLineMode;

SnapLineMode.onSetup = function (options:any) {
  // this.map._snapLineMode = SnapLineMode;
  SnapLineMode.state = <drawState>{};
  SnapLineMode._this = <drawModes>{};

  // try {
  //   document.getElementsByClassName('geobox-gl-rotate-icon')[0].checked = false;
  //   document.getElementsByClassName('geobox-gl-hole-icon')[0].checked = false;
  //   document.getElementsByClassName('geobox-gl-split-icon')[0].checked = false;
  // } catch (error) {
  // }


  const line = this.newFeature({
    type: Constants.geojsonTypes.FEATURE,
    properties: {},
    geometry: {
      type: Constants.geojsonTypes.LINE_STRING,
      // Must match DrawLineString — `[[]]` makes the first click finish as a degenerate line.
      coordinates: [],
    },
  });


  this.addFeature(line);

  const selectedFeatures = this.getSelected();
  this.clearSelectedFeatures();
  doubleClickZoom.disable(this);

  const state = <drawState>{
    map: this.map,
    line,
    currentVertexPosition: 0,
    selectedFeatures,
    direction: 'forward', // expected by DrawLineString
  };

  state.options = sanitizeSnapOptions(this._ctx.options);


  const optionsChangedCallBAck = (options:drawState["options"]) => {
    state.options = sanitizeSnapOptions(options);
  };

  state.optionsChangedCallBAck = optionsChangedCallBAck;

  this.map.on('draw.snap.options_changed', optionsChangedCallBAck);

  try {
    const snapSrc = this.map.getSource('geobox-gl-snap-marker') as Source | undefined;
    this.snapMarkerSource = snapSrc?._data ?? { features: [] };
  } catch {
    this.snapMarkerSource = { features: [] };
  }
  const center = this.map.getCenter();
  state.snappedLng = center.lng;
  state.snappedLat = center.lat;

  SnapLineMode.state = state;

  SnapLineMode._this = this;

  return state;
};

SnapLineMode.onTap = function (state:drawState, e:any) {
  // Same guard as onClick — finishing on the first vertex ends the sketch prematurely.
  if (CommonSelectors.isVertex(e) && state.currentVertexPosition >= 2) {
    return this.clickOnVertex(state, e);
  }
  this.onMouseMove(state, e);
  this.onClick(state, e);
}

SnapLineMode.onClick = function (state: drawState, e?: any) {
  // Recompute snap at the click location, then commit the locked vertex (not raw mouse).
  if (e) {
    SnapLineMode.onMouseMove.call(this, state, e);
  }
  const lng = state.snappedLng;
  const lat = state.snappedLat;

  // Require at least two committed vertices before "click last vertex to finish".
  // With position === 1 the cursor-follower sits on the first vertex, so a duplicate
  // click/tap at the same spot would otherwise end the draw immediately.
  if (state.currentVertexPosition >= 2) {
    const lastVertex: drawState['lastVertex'] = <drawState['lastVertex']>(
      state.line.coordinates[state.currentVertexPosition - 1]
    );

    state.lastVertex = lastVertex;

    if (isAtLastVertex(state, e, lng, lat)) {
      return finishSnapLine.call(this, state);
    }

  }

  if (e?.originalEvent?.detail >= 2 && state.currentVertexPosition >= 2) {
    return finishSnapLine.call(this, state);
  }

  state.line.updateCoordinate(state.currentVertexPosition, lng, lat);

  state.currentVertexPosition++;

  state.line.updateCoordinate(state.currentVertexPosition, lng, lat);
};

SnapLineMode.onMouseMove = function (state:drawState, e:any) {
  const { lng, lat } = e.lngLat;

  let minDistance = Infinity;
  let desiredCoord: number[] | undefined;
  let snapKind: SnapHitKind | undefined;
  if (state.options.snap) {
    [minDistance, desiredCoord, snapKind] = calculateSnapTolerance(this.map, e, state);
  }

  const locked = Boolean(
    state.options.snap &&
      desiredCoord &&
      Number.isFinite(minDistance) &&
      minDistance <= (state.options.snapTolerance ?? Infinity)
  );

  if (locked && desiredCoord) {
    const [snapLng, snapLat] = positionToLngLat(desiredCoord as [number, number]);
    state.snappedLng = snapLng;
    state.snappedLat = snapLat;
  } else {
    state.snappedLng = lng;
    state.snappedLat = lat;
  }

  if (state.options.snap) {
    setSnapMarkerOnMap(this.map, state.snappedLng, state.snappedLat, {
      locked,
      kind: locked ? snapKind ?? null : null,
    });
  } else {
    clearSnapMarkerOnMap(this.map);
  }

  state.line.updateCoordinate(state.currentVertexPosition, state.snappedLng, state.snappedLat);

  //   if (state.line.coordinates.length >= 2){
  //     var limitDrawLength = document.getElementById('geobox-gl-geometry-length-parameter');
  //     var limitDrawBearing = document.getElementById('geobox-gl-geometry-bearing-parameter');

  //     if (limitDrawLength){
  //       const pXY0 = state.line.getCoordinate(`${state.currentVertexPosition-1}`);
  //       const pXY0_3857 = deegrees2meters(pXY0);
  //       const pXY2_3857 = [];
  //       if (Number(limitDrawBearing.value) === 90){
  //         pXY2_3857[0] = pXY0_3857[0];
  //         pXY2_3857[1] = pXY0_3857[1]+ Number(limitDrawLength.value);
  //       }else if (Number(limitDrawBearing.value) === 270){
  //         pXY2_3857[0] = pXY0_3857[0];
  //         pXY2_3857[1] = pXY0_3857[1]- Number(limitDrawLength.value);
  //       }else {
  //         pXY2_3857[0] = pXY0_3857[0]+ Number(limitDrawLength.value)*Math.cos(Number(limitDrawBearing.value)*Math.PI/180);
  //         pXY2_3857[1] = pXY0_3857[1]+ Number(limitDrawLength.value)*Math.sin(Number(limitDrawBearing.value)*Math.PI/180);
  //       }

  //       const pXY2 = meters2degrees(pXY2_3857);
        
  //       state.snappedLng = pXY2[0];
  //       state.snappedLat = pXY2[1];
  //     }
  //   }
  // }else if (state.options.limitDrawLength && state.options.limitDrawAngle){

  //   if (state.line.coordinates.length >= 3){
  //     var limitDrawLength = document.getElementById('geobox-gl-geometry-length-parameter');
  //     var limitDrawAngle = document.getElementById('geobox-gl-geometry-angle-parameter');

  //     if (limitDrawLength){
  //       const pXY0 = state.line.getCoordinate(`${state.currentVertexPosition-2}`);
  //       const pXY0_3857 = deegrees2meters(pXY0);
  //       const pXY1 = state.line.getCoordinate(`${state.currentVertexPosition-1}`);
  //       const pXY1_3857 = deegrees2meters(pXY1);

  //       // const pXY2_3857 = deegrees2meters([lng, lat]);

  //       const pXY3_3857 = [];

  //       var angle = lineAngle(pXY0_3857, pXY1_3857);

  //       var newAngle = 180 - (Number(limitDrawAngle.value) - angle );

  //       if (newAngle === 90){
  //         pXY3_3857[0] = pXY1_3857[0];
  //         pXY3_3857[1] = pXY1_3857[1]+ Number(limitDrawLength.value);
  //       }else if (newAngle === 270){
  //         pXY3_3857[0] = pXY1_3857[0];
  //         pXY3_3857[1] = pXY1_3857[1]- Number(limitDrawLength.value);
  //       }else {
  //         pXY3_3857[0] = pXY1_3857[0]+ Number(limitDrawLength.value)*Math.cos(newAngle*Math.PI/180);
  //         pXY3_3857[1] = pXY1_3857[1]+ Number(limitDrawLength.value)*Math.sin(newAngle*Math.PI/180);
  //       }

  //       const pXY3 = meters2degrees(pXY3_3857);
      
  //       state.snappedLng = pXY3[0];
  //       state.snappedLat = pXY3[1];
  //     }
  //   }
  // }else {
  //   if (state.options.limitDrawLength) {
  //     if (state.line.coordinates.length >= 2){
  //       var limitDrawLength = document.getElementById('geobox-gl-geometry-length-parameter');
  //       if (limitDrawLength){
  //         const pXY0 = state.line.getCoordinate(`${state.currentVertexPosition-1}`);
  //         const pXY0_3857 = deegrees2meters(pXY0);
  //         const pXY1_3857 = deegrees2meters([lng, lat]);
  //         const pXY2_3857 = [];
         
  //         var dy = pXY1_3857[1]-pXY0_3857[1];
  //         var dx = pXY1_3857[0]-pXY0_3857[0];
  
  //         if (dx >= 0 ){
  //           pXY2_3857[0] = pXY0_3857[0]+ Number(limitDrawLength.value)*Math.cos(Math.atan(dy/dx));
  //           pXY2_3857[1] = pXY0_3857[1]+ Number(limitDrawLength.value)*Math.sin(Math.atan(dy/dx));
  //         }else {
  //           pXY2_3857[0] = pXY0_3857[0]- Number(limitDrawLength.value)*Math.cos(Math.atan(dy/dx));
  //           pXY2_3857[1] = pXY0_3857[1]- Number(limitDrawLength.value)*Math.sin(Math.atan(dy/dx));
  //         }
  
  //         const pXY2 = meters2degrees(pXY2_3857);
        
  //         state.snappedLng = pXY2[0];
  //         state.snappedLat = pXY2[1];
  //       }
  //     }
  //   }
  
  //   if (state.options.limitDrawBearing) {
  //     if (state.line.coordinates.length >= 2){
  //       var limitDrawBearing = document.getElementById('geobox-gl-geometry-bearing-parameter');
  //       if (limitDrawBearing){
  //         const pXY0 = state.line.getCoordinate(`${state.currentVertexPosition-1}`);
  //         const pXY0_3857 = deegrees2meters(pXY0);
  //         const pXY1_3857 = deegrees2meters([lng, lat]);
  //         const pXY2_3857 = [];

  //         if (Number(limitDrawBearing.value) !== 90 && Number(limitDrawBearing.value) !== 270){
  //           pXY2_3857[0] = pXY1_3857[0];
  //           pXY2_3857[1] = pXY0_3857[1]+ Math.tan(Number(limitDrawBearing.value)*Math.PI/180)*(pXY2_3857[0]-pXY0_3857[0]);
  //         }else {
  //           pXY2_3857[0] = pXY0_3857[0];
  //           pXY2_3857[1] = pXY1_3857[1];
  //         }
         
  //         const pXY2 = meters2degrees(pXY2_3857);
        
  //         state.snappedLng = pXY2[0];
  //         state.snappedLat = pXY2[1];
  //       }
  //     }
  //   }
  //   if (state.options.limitDrawAngle) {
  //     if (state.line.coordinates.length >= 3){
  //       var limitDrawAngle = document.getElementById('geobox-gl-geometry-angle-parameter');
  //       if (limitDrawAngle){
        
  //         const pXY0 = state.line.getCoordinate(`${state.currentVertexPosition-2}`);
  //         const pXY0_3857 = deegrees2meters(pXY0);
  //         const pXY1 = state.line.getCoordinate(`${state.currentVertexPosition-1}`);
  //         const pXY1_3857 = deegrees2meters(pXY1);

  //         const pXY2_3857 = deegrees2meters([lng, lat]);

  //         const pXY3_3857 = [];

  //         var angle = lineAngle(pXY0_3857, pXY1_3857);

        
  //         var newAngle = 180 - (Number(limitDrawAngle.value) - angle );

  //         if (newAngle !== 90 && newAngle !== 270){
  //           pXY3_3857[0] = pXY2_3857[0];
  //           pXY3_3857[1] = pXY1_3857[1]+ Math.tan(newAngle*Math.PI/180)*(pXY3_3857[0]-pXY1_3857[0]);
  //         }else {
  //           pXY3_3857[0] = pXY0_3857[0];
  //           pXY3_3857[1] = pXY2_3857[1];
  //         }
          
  //         const pXY3 = meters2degrees(pXY3_3857);
        
  //         state.snappedLng = pXY3[0];
  //         state.snappedLat = pXY3[1];
  //       }
  //     }
  //   }
  // }

  if (
    state.lastVertex
    && state.lastVertex[0] === lng
    && state.lastVertex[1] === lat
  ) {
    this.updateUIClasses({ mouse: Constants.cursors.POINTER });
  } else {
    this.updateUIClasses({ mouse: Constants.cursors.ADD });
  }
};

SnapLineMode.toDisplayFeatures = function (state:drawState, geojson:GeoJSONFeature, display:any) {
  DrawLine.toDisplayFeatures(state, geojson, display);
};

SnapLineMode.onStop = function (state:drawState) {
  try {
    if (state?.optionsChangedCallBAck) {
      (this.map as any).off('draw.snap.options_changed', state.optionsChangedCallBAck);
    }
  } catch {
    /* ignore */
  }
  clearSnapMarkerOnMap(this.map);

  // Do not force "grab" here — Map._syncCursorFromDrawMode / host tools own the cursor.
  // Forcing grab after the first vertex made profile/inquiry line draw look cancelled.

  DrawLine.onStop.call(this, state);

  SnapLineMode.state = <drawState>{};
  SnapLineMode._this = <drawModes>{};
};

SnapLineMode.onKeyUp = function(state:drawState, e:any) {
  if (e.key === 'Enter') {
    return finishSnapLine.call(this, state);
  } else if (e.key === 'Escape') {
    // this.deleteFeature([state.line.id], { silent: true });
    // this.changeMode(Constants.modes.SIMPLE_SELECT);


    // if (state.currentVertexPosition > 2) {
      //remove the last point
      state.currentVertexPosition--;
      state.line.removeCoordinate(`${state.currentVertexPosition}`);
      // console.log(state.currentVertexPosition,'  ', state.snappedLng,'  ', state.snappedLat)

      state.line.updateCoordinate(state.currentVertexPosition, state.snappedLng, state.snappedLat);
   
    // } 
    // else {
    //   this.deleteFeature([state.line.id], { silent: true });
    //   this.changeMode(Constants.modes.SIMPLE_SELECT);
    // }
  }
};


SnapLineMode.removeLastVertex = function(state:drawState, _this:drawModes) {
  try {
    // if (state.currentVertexPosition > 2) {

      state.currentVertexPosition--;
      state.line.removeCoordinate(`${state.currentVertexPosition}`);
   
      state.line.updateCoordinate(state.currentVertexPosition, state.snappedLng, state.snappedLat);
  
      state.line.updateCoordinate(state.currentVertexPosition, 35, 53);

      _this.onMouseMove(state,{lngLat:{lng:35, lat:53}})

  
  
      // _this.map._update()
  
      // _this.map.getSource('mapbox-gl-draw-hot').setData(_this.map.getStyle().sources['mapbox-gl-draw-hot']['data'])
  
      // _this.map.getSource('mapbox-gl-draw-cold').setData(_this.map.getStyle().sources['mapbox-gl-draw-cold']['data'])
  
    // }
  } catch (error) {}
   
};

SnapLineMode.finishDraw = function(state:drawState, _this:drawModes) {
  try {
    finishSnapLine.call(_this, state);
  } catch (error) { 
  }
};

SnapLineMode.cancelDraw = function(state:drawState, _this:drawModes) {
  try {
    _this.deleteFeature([state.line.id], { silent: true });
    _this.changeMode(Constants.modes.SIMPLE_SELECT);
  } catch (error) {
  }
};

// export default SnapLineMode;
